import React from "react";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { useSelector } from "react-redux";
import { routes } from "../../../../constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import "./payments-billing-list.scss";
import ListingTable from "../../../../common/components/listing-table/list-table";
import { getPaymentBillingList } from "../../../../api/admin/order-management";
import { headers } from "./payment-billing-helper";
import AdminPaginationComponent from "../../../../common/components/admin-pagination-component/admin-pagination-component";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { AlertComponent } from "../../../../common/components";
import ServiceListSearchAndFilters from "./payment-billing-search-filters";
import _ from "lodash";
import dayjs from "dayjs";
import PaymentStatus from "../../../preneed-section/payments-and-billing/payment-status";

const PaymentsBilling: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);
  const breadcrumbLinkForList = [
    {
      label: "Order Management",
      link: routes.ADMIN_ORDER_MANAGEMENT,
      handleClick: () => {},
      disablePointerEvents: true,
    },
  ];
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForList);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [error, setError] = useState("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<any>(
    location?.state?.searchText || "",
  );
  const [activeFilters, setActiveFilters] = useState<any>(
    location?.state?.activeFilters || {},
  );
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef(false);
  const pageSize = 10;

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1);
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    [],
  );

  const fetchPaymentBillingList = async (
    page: number,
    search: string,
    filters: any,
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        page,
        pageSize,
        timezone: userTimeZone,
      };
      if (search && search.trim() !== "") {
        params.searchText = search.trim();
      }
      if (filters?.providerIds) {
        params.providerIds = filters.providerIds;
      }
      if (filters?.plannerIds) {
        params.plannerIds = filters.plannerIds;
      }
      if (filters?.fromDate && filters?.toDate) {
        params.fromDate = dayjs(filters.fromDate).startOf("day").toISOString();
        params.toDate = dayjs(filters.toDate).endOf("day").toISOString();
      }

      const response = await getPaymentBillingList(params);
      if (response?.status === 200 || response?.data?.success) {
        setData(response?.data?.data?.result || []);
        setTotalRecords(response?.data?.data?.totalCount || 0);
      } else {
        setAlertType("error");
        setAlertMessage("Failed to fetch payments");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to fetch Payment Billings List.",
      );
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchPaymentBillingList(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };

  const EMPTY_FILTERS = {
    providerIds: undefined,
    plannerIds: undefined,
    fromDate: "",
    toDate: "",
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item: any) => {
        return {
          id: item.id,
          orderId: item?.orderId,
          businessName: item?.businessName
            ? item.businessName.replace(/\b\w/g, (c: string) => c.toUpperCase())
            : "-",
          customerName: item?.customerName
            ? item.customerName.replace(/\b\w/g, (c: string) => c.toUpperCase())
            : "-",
          business: item?.business,
          orderDate: item?.orderDate
            ? dayjs(item.orderDate).format("DD/MM/YYYY")
            : "-",
          totalAmount: `$${item.totalAmount || 0}`,
          partnerEarning: `$${item.partnerEarning || 0}`,
          afpCommission: `$${item.afpCommission || 0}`,
          status: item?.payment?.status || "-",
        };
      });
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (
      location.state &&
      location.state.alertMessage &&
      location?.state?.fromView
    ) {
      setAlertMessage(location.state.alertMessage);
      setAlertType(location.state.alertType || "info");
      setAlertOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state && location.state.searchText) {
      const searchValue = location.state.searchText;
      setSearchText(searchValue);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (
      location.state &&
      Object.keys(location?.state?.activeFilters || {}).length > 0
    ) {
      const filters = location.state.activeFilters;
      setActiveFilters(filters);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;

    const {
      fromDate,
      toDate,
      providerIds,
      plannerIds,
      providerName,
      plannerName,
      ...rest
    } = activeFilters;

    if (fromDate || toDate) count += 1;
    if (providerIds) count += 1;
    if (plannerIds) count += 1;

    const otherActiveFilters = Object.values(rest).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0),
    ).length;

    return count + otherActiveFilters;
  }, [activeFilters]);

  const noResultsTxt = useMemo(() => {
    if (loading) {
      return "Loading Payment requests...";
    }
    if (searchText && appliedFilterCount === 0) {
      return "No Payment Billing record found for search criteria.";
    }
    if (!searchText && appliedFilterCount > 0) {
      return "No Payment Billing record found matching filter criteria.";
    }
    if (searchText && appliedFilterCount > 0) {
      return "No Payment Billing record found for search and filter criteria.";
    }
    return "No Payment Billing record found";
  }, [loading, searchText, appliedFilterCount]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="payment-billing-main-wrapper">
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <ProfileHeader breadcrumbLink={breadcrumbLink} />
          <div
            className="payment-billing-filter-and-list-wrapper"
            style={{
              display: !isViewOpen ? "flex" : "none",
              flexDirection: "column",
              height: "100%",
              paddingBottom: totalRecords === 0 ? "10px" : "0px",
            }}
          >
            <div className="list-search-and-filters service-list-search-and-filters">
              <ServiceListSearchAndFilters
                onSearch={handleSearch}
                onFilterChange={(filters) => {
                  setActiveFilters(filters);
                  setCurrentPage(1);
                }}
                onResetFilters={() => {
                  setActiveFilters(EMPTY_FILTERS);
                  setCurrentPage(1);
                }}
                filterValues={activeFilters}
                appliedFiltersCount={appliedFilterCount}
                isFilterOpen={isOpenFilter}
                setIsFilterOpen={setIsOpenFilter}
                searchRef={searchRef}
                isReset={isResetSearch}
                localSearchText={searchText}
                placeholder="Search by Customer Name, Order ID"
              />
            </div>
            <div className="payment-billing-listing-table-wrapper">
              <ListingTable
                headers={headers}
                data={formattedData?.map((item) => ({
                  ...item,
                  status: (
                    <PaymentStatus
                      status={
                        item.status !== "-"
                          ? item.status.charAt(0).toUpperCase() +
                            item.status.slice(1)
                          : ""
                      }
                    />
                  ),
                }))}
                onRowClick={(row) => {
                  navigate(
                    `${routes.ADMIN_VIEW_ORDER.replace(":id", row.id)}`,
                    {
                      state: {
                        orderId: row.orderId,
                        searchText: searchText,
                        activeFilters: activeFilters,
                        customerName: row.customerName,
                      },
                    },
                  );
                }}
                noResultsText={noResultsTxt}
              />
              <div
                style={{ display: isOpenFilter ? "flex" : "none" }}
                className="payment-billing-filter-options filters-options-main"
              ></div>
            </div>
            {totalRecords > 0 && (
              <AdminPaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>

        <AlertComponent
          open={alertOpen}
          type={alertType}
          message={alertMessage || ""}
          onClose={() => setAlertOpen(false)}
        />
      </div>
    </>
  );
};

export default PaymentsBilling;
